import { AddNameAndEmailForm } from "@/common/commonComponents";
import { RadioFieldModel } from "@/common/commonForms";
import { Alert } from "@/components-v2/Alert";
import { Box, VBox } from "@/components-v2/Box";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { Icon } from "@/components-v2/Icon";
import { InfoButton } from "@/components-v2/InfoButton";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { Table } from "@/components-v2/Table";
import { ChangeArticlesOfAssociation } from "@/dbot/changeArticlesOfAssociation/changeArticlesOfAssociationComponents";
import { IncreaseNominalValue } from "@/dbot/common/commonCapitalIncreaseComponents";
import { exerciseSubscriptions, OptionExercise } from "@/dbot/optionExercise/optionExerciseDomain";
import {
  ArticleSelectorQuestion,
  DateQuestion,
  FromToPeriod,
  NumberQuestion,
  YesNoQuestion,
} from "@/dbot/common/commonComponents";
import { useLocaleFormat, useTranslation } from "@/stores/RootStoreContext";
import { shareTypeName } from "@/common/share";
import { CURRENCY_NOK } from "@/utils/currency";
import { compact } from "lodash";
import { observer } from "mobx-react";
import { FC, useEffect } from "react";
import { ContributionInKind } from "./ContributionInKind";
import DebtContribution from "./DebtContribution";
import { GuaranteeConsortium } from "./GuaranteeConsortium";
import { IncreaseNominalValueShareContribution } from "./IncreaseNominalValue";
import { IncreaseOfShareCapitalForm } from "./increaseOfShareCapitalDomain";
import { IsAccountGiven } from "./IsAccountGiven";
import { IsDisposableAccount } from "./IsDisposableAccount";
import { ReceiveShareholderRights } from "./ReceiveShareholderRights";
import SelectShareTypesForIssue from "./SelectShareTypesForIssue";
import ShareCapitalIncreaseMethod, {
  AskToAddBoardStatementDocumentDialog,
} from "./ShareCapitalIncreaseMethod";
import ShareIssueOrIncreaseNominalValue from "./ShareIssueOrIncreaseNominalValue";
import ShowPowerOfAttorney from "./ShowPowerOfAttorney";
import ShowPreferentialRight from "./ShowPreferentialRight";
import { SpecialCircumstances } from "./SpecialCircumstances";
import { SubscribersDialog, SubscribersTitle } from "./subscribersDialogComponents";
import { openSubscribersDialog } from "./subscribersDialogDomain";
import { SubscriberDialog } from "./SubscribersStatus";
import { Warning } from "./Warning";

const IncreaseOfShareCapitalFormView: FC<{
  form: IncreaseOfShareCapitalForm;
  isDetailsOpen?: boolean;
}> = observer(({ form, isDetailsOpen }) => {
  const { t } = useTranslation();

  return (
    <>
      <VBox gap={"md"}>
        <VBox
          sticky
          bg={isDetailsOpen ? true : "alt1"}
          className={
            // A hack to make the panel "grow" beyond the horizontal padding of the container
            "z-10 -mx-4"
          }
          stuckClassName={"border-b"}
          padding
        >
          {form.optionExercise ? (
            <OptionExerciseSubscribers form={form} exercise={form.optionExercise.exercise} />
          ) : (
            <>
              <Button
                variant={form.isSubscriptionInProgress ? "secondary" : "primary"}
                icon={"grid"}
                label={<SubscribersTitle form={form} />}
                onClick={(event) =>
                  openSubscribersDialog(form, { applyQuickSettings: event.shiftKey })
                }
              />
              {form.subscriptionCompletionError && <Alert {...form.subscriptionCompletionError} />}
            </>
          )}
        </VBox>

        {form.showSoleShareholderQuickAction && (
          <Box>
            <Button
              variant={"basic"}
              size={"sm"}
              icon={"plus"}
              label={form.shareIssue.soleShareholder?.name}
              onClick={() => form.shareIssue.addSoleShareholder()}
            />
          </Box>
        )}

        <Box fontWeight={"bold"}>{t("dbot.setup")}</Box>

        <YesNoQuestion
          question={t("decisions.increaseShareCapitalAgainstCash.usePowerOfAttorney")}
          field={form.usePowerOfAttorney}
          onChange={(e) => form.setUsePowerOfAttorney(e)}
          isLocked={form.isProjectLocked}
        />
        {form.showPowerOfAttorney && <ShowPowerOfAttorney form={form} />}

        {form.showShareIssueOrIncreaseNominalValue && (
          <>
            {!form.isLinkedToOptionExercise && <ShareCapitalIncreaseMethod form={form} />}
            {form.hasChosenIncreaseMethod && !form.isLinkedToOptionExercise && (
              <ShareIssueOrIncreaseNominalValue form={form} />
            )}
            {form.showShareIssue && (
              <>
                {form.showShareIssueNominalValue && (
                  <NumberQuestion
                    question={t("transactions.issueTransaction.nominal-amount")}
                    prefix={CURRENCY_NOK}
                    field={form.shareIssue.nominalValue}
                    isLocked={form.isProjectLocked}
                  />
                )}
                {!form.isLinkedToOptionExercise && (
                  <SelectShareTypesForIssue
                    model={form.shareIssue}
                    disableIndividualSharePrice={form.isPreferentialRight}
                  />
                )}
                {form.showPreferentialRights && <ShowPreferentialRight form={form} />}
              </>
            )}
            {form.showIncreaseNominalValue && (
              <>
                <IncreaseNominalValue
                  increaseInNominalValue={form.increaseNominalValue.increaseInNominalValue}
                  currentNominalValue={form.currentNominalValue}
                  currentNumberOfShares={form.currentNumberOfShares}
                  isProjectLocked={form.isProjectLocked}
                />
                {!form.isNewNominalValueValid && (
                  <Warning
                    message={t(
                      "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.newNominal-helperText",
                    )}
                  />
                )}
                {form.isNewNominalValueValid && !form.isNewShareCapitalValid && (
                  <Warning
                    message={t(
                      "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.newShareCapital-helperText",
                    )}
                  />
                )}
                {form.showIncreaseContributionPerShare && (
                  <IncreaseNominalValueShareContribution form={form} />
                )}
              </>
            )}
            {form.selectedSubscriber && (
              <SubscriberDialog
                selectedSubscriber={form.selectedSubscriber}
                dialogState={form.selectedSubscriberDialogState}
                close={form.onCloseSelectedSubscriberDialog}
                isLocked={form.isProjectLocked}
              />
            )}

            {form.showDebtConversion && <DebtContribution form={form} />}
            {form.showContributionInKind && <ContributionInKind form={form} />}
            {form.showIncludeSubscribersAsAttachment && (
              <YesNoQuestion
                question={t("decisions.increaseShareCapitalAgainstCash.subscribersAsAttachment")}
                field={form.isIncludeSubscribersAsAttachment}
                isLocked={form.isProjectLocked}
              />
            )}

            {form.showSubscriptionForm && (
              <>
                <YesNoQuestion
                  question={
                    form.showShareIssue
                      ? t("decisions.increaseShareCapitalAgainstCash.newShares.subscriptionForm")
                      : t(
                          "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.subscriptionForm",
                        )
                  }
                  infoText={t(
                    "decisions.increaseShareCapitalAgainstCash.subscriptionForm-infoText",
                  )}
                  field={form.isSubscriptionForm}
                  onChange={(e) => form.setIsSubscriptionForm(e)}
                  isLocked={form.isProjectLocked}
                />
                {form.isSubscriptionForm.value && (
                  <FromToPeriod
                    label={t("decisions.increaseShareCapitalAgainstCash.subscriptionFormPeriod")}
                    from={form.periodForSubscription.from}
                    to={form.periodForSubscription.to}
                    isLocked={form.isProjectLocked}
                  />
                )}
                {form.showIsSubscribersSigning && (
                  <>
                    <YesNoQuestion
                      question={t(
                        "decisions.increaseShareCapitalAgainstCash.isOtherSigningForSubscribers",
                      )}
                      field={form.isOtherSigningForAllSubscribers}
                      isLocked={form.isProjectLocked}
                    />
                    {form.showSignersForAllSubscribers && <SignersForAllSubscribers form={form} />}
                  </>
                )}
                {form.showDeadlineForSubscription && (
                  <>
                    <DateQuestion
                      question={t("decisions.increaseShareCapitalAgainstCash.payment.deadline")}
                      label={t("decisions.enter-date")}
                      field={form.deadlineForSubscription}
                      isLocked={form.isProjectLocked}
                    />
                    {form.deadlineForSubscriptionNotEmpty && (
                      <>
                        {form.showAccountInformation && (
                          <>
                            <IsDisposableAccount
                              isDisposableAccount={form.isDisposableAccount}
                              isProjectLocked={form.isProjectLocked}
                            />
                            <IsAccountGiven
                              isAccountGiven={form.isAccountGiven}
                              isProjectLocked={form.isProjectLocked}
                              accountNumber={form.accountNumber}
                            />
                          </>
                        )}
                        {form.showShareholderRights && (
                          <ReceiveShareholderRights
                            shareholderRights={form.shareIssue.shareholderRights}
                            isProjectLocked={form.isProjectLocked}
                          />
                        )}
                        <div data-testid={"estimatedCosts"}>
                          <NumberQuestion
                            field={form.estimatedCosts}
                            question={t("decisions.increaseShareCapitalAgainstCash.estimatedCosts")}
                            label={t("decisions.enter-amount")}
                            prefix={CURRENCY_NOK}
                            isLocked={form.isProjectLocked}
                          />
                        </div>
                        {form.showGuaranteeConsortium && <GuaranteeConsortium form={form} />}

                        <SpecialCircumstances
                          model={form.specialCircumstancesUponSubscriptionModel}
                          specialCircumstancesTexts={form.specialCircumstancesUponSubscriptionTexts}
                          question={t(
                            "decisions.increaseShareCapitalAgainstCash.circumstances.questionText",
                          )}
                          addedExplanationQuestion={t(
                            "decisions.increaseShareCapitalAgainstCash.circumstancesDescription",
                          )}
                          isProjectLocked={form.isProjectLocked}
                        />
                        <SpecialCircumstances
                          model={form.specialCircumstancesAfterLastApprovedAnnualAccountModel}
                          specialCircumstancesTexts={
                            form.specialCircumstancesAfterLastApprovedAnnualAccountTexts
                          }
                          question={t(
                            "decisions.increaseShareCapitalAgainstCash.circumstancesAfterApprovedAnnualAccount.questionText",
                          )}
                          addedExplanationQuestion={t(
                            "decisions.increaseShareCapitalAgainstCash.circumstancesAfterApprovedAnnualAccountDescription",
                          )}
                          isProjectLocked={form.isProjectLocked}
                        />
                        <UpdateArticlesAutomatically
                          updateArticlesAutomatically={form.updateArticlesAutomatically}
                          isProjectLocked={form.isProjectLocked}
                        />
                        {form.isUpdateArticlesAutomatically && (
                          <ArticleSelectorQuestion
                            question={t(
                              "decisions.increaseShareCapitalAgainstCash.which-paragraph",
                            )}
                            model={form.articleOfAssociationNumber}
                            isLocked={form.isProjectLocked}
                            onChange={form.articleOfAssociationNumber.setSelected}
                          />
                        )}
                        {form.isUpdateArticlesManually && (
                          <ChangeArticlesOfAssociation model={form.articleChangeModel} />
                        )}
                        {form.isCapitalCallEnabled && (
                          <VBox>
                            <div>
                              {t(
                                "decisions.increaseShareCapitalAgainstCash.updateCommitmentAutomatically.title",
                              )}
                            </div>
                            <MRadioButtonGroup
                              name={"updateCommitmentAutomatically"}
                              model={form.updateCommitmentAutomatically}
                              disabled={form.isProjectLocked}
                            />
                          </VBox>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </VBox>
      {!form.subscibersDialogState && <AskToAddBoardStatementDocumentDialog form={form} />}
      {form.subscibersDialogState && (
        <SubscribersDialog
          form={form}
          state={form.subscibersDialogState}
          subDialogs={
            <>
              <AskToAddBoardStatementDocumentDialog form={form} />
            </>
          }
        />
      )}
    </>
  );
});

const OptionExerciseSubscribers = observer(function OptionExerciseSubscribers({
  form,
  exercise,
}: {
  form: IncreaseOfShareCapitalForm;
  exercise: OptionExercise;
}) {
  const { t, f } = useTranslation();
  const subscriptions = exerciseSubscriptions(exercise);

  return (
    <VBox gap={"sm"}>
      <Alert
        type={"info"}
        message={
          <VBox gap={"xs"} alignItems={"start"}>
            <span>{t("decisions.optionExercise.linked-subscribers")}</span>
            <LinkButton
              label={t("decisions.optionExercise.open-exercise")}
              onClick={form.openOptionExercise}
            />
          </VBox>
        }
      />
      {subscriptions.length > 0 && (
        <Table variant={"COMPACT"}>
          <thead>
            <tr>
              <th align={"left"}>{t("signing.subscription.subscriber")}</th>
              <th align={"left"}>{t("subscription.sharetype")}</th>
              <th align={"right"}>{t("decisions.number-of-shares")}</th>
              <th align={"right"}>{t("transactions.price-share")}</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.map((subscription, index) => (
              <tr key={index}>
                <td>{subscription.subscriberName}</td>
                <td>{t(shareTypeName(subscription.shareTypeCode))}</td>
                <td align={"right"}>{f.formatNumber(subscription.numberOfShares)}</td>
                <td align={"right"}>{f.formatNumber(subscription.price)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </VBox>
  );
});

export const UpdateArticlesAutomatically = observer(function ({
  updateArticlesAutomatically,
  isProjectLocked,
}: {
  updateArticlesAutomatically: RadioFieldModel<boolean | undefined>;
  isProjectLocked: boolean;
}) {
  const { t } = useTranslation();
  return (
    <VBox>
      <Box>
        <div>
          {t("decisions.increaseShareCapitalAgainstCash.updateArticlesAutomatically.title")}
        </div>
        <InfoButton
          icon={"info"}
          infoText={{
            key: "decisions.increaseShareCapitalAgainstCash.updateArticlesAutomatically.info",
          }}
        />
      </Box>
      <MRadioButtonGroup
        name={"updateArticlesAutomatically"}
        model={updateArticlesAutomatically}
        disabled={isProjectLocked}
      />
    </VBox>
  );
});

export const SignersForAllSubscribers: FC<{
  form: IncreaseOfShareCapitalForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <>
      <div>
        {t("decisions.increaseShareCapitalAgainstCash.signersForAllSubscribers.questionText")}
      </div>
      {form.signersForAllSubscribers.map((p) => (
        <Box key={p.id} alignItems={"center"} marginLeft={"md"}>
          <div>
            {p.name} ({p.email})
          </div>
          <IconButton
            icon={"trash"}
            tooltip={t("remove")}
            onClick={() => form.removeSignerForAllSubscribers(p)}
            disabled={form.isProjectLocked}
            compact
          />
        </Box>
      ))}
      <Box>
        {form.addSignerForAllSubscribersModel ? (
          <AddNameAndEmailForm
            form={form.addSignerForAllSubscribersModel}
            submit={form.submitSignerForAllSubscribers}
            cancel={form.cancelSignerForAllSubscribers}
          />
        ) : (
          <LinkButton
            icon={"plus"}
            label={t(
              "decisions.increaseShareCapitalAgainstCash.signersForAllSubscribers.addSigner",
            )}
            onClick={() => form.addSignerForAllSubscribers()}
            disabled={form.isProjectLocked}
          />
        )}
      </Box>
      {form.isSignersForAllSubscribersEmpty && (
        <Alert
          message={t("decisions.increaseShareCapitalAgainstCash.signersForAllSubscribers.info")}
          type={"info"}
        />
      )}
    </>
  );
});

export const IncreaseOfShareCapitalView: FC<{
  form: IncreaseOfShareCapitalForm;
  showOpenDetailsButton: boolean;
  openDetails: () => void;
  isDetailsOpen?: boolean;
  hasValidationError?: boolean;
}> = observer(({ form, showOpenDetailsButton, openDetails, isDetailsOpen, hasValidationError }) => {
  const { t } = useTranslation();
  useEffect(() => {
    return form.init();
  }, [form]);

  return showOpenDetailsButton ? (
    <VBox gap={"md"}>
      {hasValidationError && (
        <Alert
          message={{
            key: "dbot.status-and-next-step.error",
            params: { decisionType: t("decisionType.increaseOfShareCapital.name") },
          }}
          type={"error"}
        />
      )}
      <Button
        variant={"secondary"}
        label={t("decisions.increaseShareCapitalAgainstCash.edit-settings")}
        onClick={openDetails}
      />
    </VBox>
  ) : (
    <IncreaseOfShareCapitalFormView form={form} isDetailsOpen={isDetailsOpen} />
  );
});

export const ShareCapitalSummaryTable = observer(
  ({ form }: { form: IncreaseOfShareCapitalForm }) => {
    const { t } = useTranslation();
    const { formatAmount, formatNumber } = useLocaleFormat();

    const rows = compact([
      {
        label: t("decisions.increaseShareCapital.summary.numberOfShares"),
        before: formatNumber(form.currentNumberOfShares),
        afterMinimum: formatNumber(form.getNewNumberOfShares("minimum")),
        afterMaximum: formatNumber(form.getNewNumberOfShares("maximum")),
      },
      {
        label: t("decisions.increaseShareCapital.summary.nominalValue"),
        before: formatAmount(CURRENCY_NOK, form.currentNominalValue),
        afterMinimum: undefined,
        afterMaximum: formatAmount(CURRENCY_NOK, form.newNominalValue),
      },
      {
        label: t("decisions.increaseShareCapital.summary.totalShareCapital"),
        before: formatAmount(CURRENCY_NOK, form.currentShareCapital),
        afterMinimum: formatAmount(CURRENCY_NOK, form.getNewShareCapital("minimum")),
        afterMaximum: formatAmount(CURRENCY_NOK, form.getNewShareCapital("maximum")),
      },
      form.totalPaidInPremium && {
        label: t("decisions.increaseShareCapital.summary.paid-in-premium"),
        before: formatAmount(CURRENCY_NOK, form.totalPaidInPremium),
        afterMinimum: undefined,
        afterMaximum: formatAmount(CURRENCY_NOK, form.newTotalPaidInPremium),
      },
      {
        label: t("decisions.increaseShareCapital.summary.paid-in-capital"),
        before: formatAmount(CURRENCY_NOK, form.totalPaidInShareCapital),
        afterMinimum: undefined,
        afterMaximum: formatAmount(CURRENCY_NOK, form.newTotalPaidInShareCapital),
      },
      form.totalPaidInShareCapital && {
        label: t("decisions.increaseShareCapital.summary.numberOfShareholders"),
        before: formatNumber(form.shareholdersWithShares.length),
        afterMinimum: undefined,
        afterMaximum: formatNumber(form.numberOfShareholdersAfter),
      },
    ]);

    return (
      <VBox gap={"md"}>
        <Table variant={"COMPACT"}>
          <thead>
            <tr className="text-left subtle">
              <th>{t("decisions.increaseShareCapital.summary.title")}</th>
              <th align={"right"}>{t("decisions.increaseShareCapital.summary.before")}</th>
              {form.isShareCapitalIncreaseRange ? (
                <>
                  <th align={"right"}>
                    {t("decisions.increaseShareCapital.summary.after.minimum")}
                  </th>
                  <th align={"right"}>
                    {t("decisions.increaseShareCapital.summary.after.maximum")}
                  </th>
                </>
              ) : (
                <th align={"right"}>{t("decisions.increaseShareCapital.summary.after")}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>{row.label}</td>
                <td align={"right"}>{row.before}</td>
                {form.isShareCapitalIncreaseRange ? (
                  <>
                    <td align={"right"}>{row.afterMinimum}</td>
                    <td align={"right"}>{row.afterMaximum}</td>
                  </>
                ) : (
                  <td align={"right"}>{row.afterMaximum}</td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </VBox>
    );
  },
);

export const IncreaseOfShareCapitalHeadingAction = observer(
  ({ form }: { form: IncreaseOfShareCapitalForm }) => {
    const { formatAmount } = useLocaleFormat();

    return (
      <Box gap={"lg"} alignItems={"center"}>
        <Box alignItems={"center"}>
          <Icon name={"trend-up"} />
          {form.isShareCapitalIncreaseRange ? (
            <Box>
              {formatAmount(CURRENCY_NOK, form.getIncreaseInShareCapital("minimum"))} -{" "}
              {formatAmount(CURRENCY_NOK, form.getIncreaseInShareCapital("maximum"))}
            </Box>
          ) : (
            <Box>{formatAmount(CURRENCY_NOK, form.getIncreaseInShareCapital("minimum"))}</Box>
          )}
        </Box>
        <InfoButton infoText={<ShareCapitalSummaryTable form={form} />} />
      </Box>
    );
  },
);
